import { mapState } from 'pinia'

import { setStore } from '@/sub-main'
import { isLan, isWan, setSessionStorage } from '@Utils/system'
import componentName from '@Constant/componentName'
import { URLS } from '@Constant/index'
import { isString, isObject, deepCopy } from '@Utils/index'
import { SPM_ID } from '@Constant/behaviorTrack'
import { reportConfig, ClickPvRelation, parserClickTrackData } from '@Utils/report'
import translate from '@bilibili-business/textformat-sdk'
import { useGlobalStore } from '@/store'

export const useMixin = app => {
  app.mixin({
    data() {
      return {
        componentNames: componentName,
        urls: URLS
      }
    },
    computed: {
      isLanLogin() {
        return isLan()
      },
      isWanLogin() {
        return isWan()
      },
      isGrey() {
        // 灰度标识流程 12.28 前端全量
        return 1
      },
      matched: {
        cache: false,
        get() {
          return (this.$router && this.$router.currentRoute.value.matched) || []
        }
      },
      ...mapState(useGlobalStore, ['accountId', 'accountInfo'])
    },
    methods: {
      t(...params) {
        return translate(...params)
      },
      toTop() {
        const dom = document.getElementById('content-main')

        if (dom) {
          dom.scrollTo({ top: 0, behavior: 'instant' })
        }
      },
      setStore(key, value) {
        setStore(key, value)
      },
      toNewTab(url = '', target = '_blank') {
        window.open(url, target)
      },
      pushRouter(option, onComplete, onAbort) {
        this.$router.push(option, onComplete, onAbort)
      },
      replaceRouter(option, onComplete, onAbort) {
        this.$router.replace(option, onComplete, onAbort)
      },
      newTabPushRouter(to, from, append = false) {
        const { href } = this.$router.resolve(to, from, append)

        window.open(href, '_blank')
      },
      setLocalStorage(id, value) {
        window.localStorage.setItem(id, value)
      },
      getLocalStorage(id) {
        return window.localStorage.getItem(id)
      },
      setSessionStorage(id, value) {
        window.sessionStorage.setItem(id, value)
      },
      getSessionStorage(id) {
        return window.sessionStorage.getItem(id)
      },
      getEncodeContentFromLocal(key) {
        try {
          const content = window.sessionStorage.getItem(key)
          var params = decodeURIComponent(content) || JSON.stringify({})
          const gameObject = params ? JSON.parse(params) : {}
          return gameObject
        } catch (e) {
          return {}
        }
      },
      removeSessionStorage(id) {
        window.sessionStorage.removeItem(id)
      },
      removeLocalStorage(id) {
        window.localStorage.removeItem(id)
      },
      isJSON(str) {
        if (typeof str === 'string') {
          try {
            JSON.parse(str)
            return true
          } catch (e) {
            return false
          }
        }
      },
      getCookie(name) {
        if (document.cookie.length > 0) {
          let start = document.cookie.indexOf(name + '=')
          if (start !== -1) {
            start = start + name.length + 1
            let end = document.cookie.indexOf(';', start)
            if (end === -1) end = document.cookie.length
            let str = unescape(document.cookie.substring(start, end))
            if (this.isJSON(str)) {
              str = JSON.parse(str)
            }
            return str
          }
        }
        return ''
      },
      clearCookie(name) {
        var exdate = new Date()
        exdate.setDate(exdate.getDate() + -1)
        document.cookie = name + '=' + escape('') + ';expires=' + exdate.toGMTString() + ';path=/'
      },
      /**
       * @description 重置表单
       * @param name
       */
      resetForm(name) {
        this.$refs[name] && this.$refs[name].resetFields()
      },
      /**
       * @description 表单验证
       * @param name
       */
      validForm(name) {
        const _this = this
        return new Promise(function (resolve, reject) {
          _this.$refs[name].validate(function (valid) {
            if (valid) {
              return resolve(valid)
            } else {
              _this.$Message.error('信息有误，请核对后提交~')
              _this.$emit('on-invalid', valid)
              return reject(valid)
            }
          })
        })
      },
      /**
       * @description 表单单个字段验证
       * @param name 表单名
       * @param field 字段名
       */
      validFormField(name, field) {
        this.$refs[name].validateField(field)
      },
      /**
       * @description 上报页面pv数据
       * @param referUrl
       * @param msg
       * @param targetUrl
       */
      reportPagePv(referUrl = window.document.referrer, msg = '', targetUrl = window.location.href) {
        this.setSpmId(SPM_ID)
        if (window.reportObserver) {
          // 兼容未用logAuto的页面
          const params = this.mergeSpmData(deepCopy(msg))

          if (params && params.source) {
            const clickFrom = parserClickTrackData(params.source)

            if (clickFrom) {
              params.click_from = clickFrom
            }
          }

          window.reportObserver.sendPV(encodeURIComponent(this.getCompleteUrl(referUrl)), JSON.stringify(params), this.getCompleteUrl(targetUrl))
        }
      },
      /**
       * @description 自定义上报数据
       * @param key
       * @param data
       * @param behavior 特殊行为，rid代表带上request_id
       */
      reportElementSpmData(key, data = {}, behavior = {}) {
        this.setSpmId(SPM_ID)
        if (window.reportObserver && key) {
          const extra = {}
          if (behavior.rid) {
            extra.request_id = reportConfig.globalRequestId
          }

          // 链路追踪
          if (ClickPvRelation[key]) {
            setSessionStorage('pv-click-from', key)
          }

          window.spmReportData[key] = this.mergeSpmData({ ...data, ...extra })
          window.reportObserver.forceCommit()
        }
      },
      /**
       * @description 设置上报的spmid
       * @param spmId
       */
      setSpmId(spmId = '') {
        if (window.reportObserver) {
          window.reportObserver.setSPM_id(spmId || SPM_ID)
        }
      },
      /**
       * @description 构建埋点上报数据
       * @param data
       * @returns {(*&{account_id: string|*, account_role: *, department_type: *, mid: string|string|*})|{account_id: string|*, account_role: *, department_type: *, mid: string|string|*}}
       */
      mergeSpmData(data = null) {
        const accountInfo = this.accountInfo || {}
        const datas = {
          mid: accountInfo.mid,
          account_id: accountInfo.account_id,
          account_role: accountInfo.account_role,
          department_type: accountInfo.department_type
        }
        if (isObject(data)) {
          return {
            ...datas,
            ...deepCopy(data)
          }
        }
        if (isString(data)) {
          datas.source = data
        }
        return datas
      },
      /**
       * @description 构建完整url
       * @param url
       * @returns {string|*}
       */
      getCompleteUrl(url) {
        if (/^http(s)?:/.test(url)) {
          return url
        }
        if (/^\/.+/.test(window.location.pathname)) {
          return `${window.location.origin}${window.location.pathname}#${url}`
        }
        return `${window.location.origin}/#${url}`
      }
    }
  })
}
